import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Crown, Download, Mail, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { safeFormat } from "@/lib/dateUtils";
import { toast } from "sonner";

const fmtGBP = (v) => `£${Number(v || 0).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

export function BestClientsReport() {
  const [days, setDays] = useState(365);
  const [sortBy, setSortBy] = useState('spend');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/reports/best-clients?days=${days}&sort_by=${sortBy}&limit=25`);
        if (!cancelled) setData(res.data);
      } catch (e) { toast.error('Failed to load best clients'); }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [days, sortBy]);

  const exportCSV = () => {
    if (!data?.clients) return;
    const rows = [
      ['Best Clients', `Last ${days} days, sorted by ${sortBy}`], [],
      ['Name', 'Email', 'Visits', 'Total Spend', 'Avg Spend', 'Last Visit', 'Status'],
      ...data.clients.map(c => [c.name, c.email, c.visits, c.total_spent, c.avg_spend, c.last_visit?.slice(0, 10), c.status])
    ];
    const csv = rows.map(r => r.map(v => `"${(v ?? '').toString().replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `best-clients-${days}d.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <p className="text-xs text-muted-foreground mb-1">Window</p>
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
            <SelectTrigger className="w-40 rounded-xl" data-testid="best-clients-window"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 3 months</SelectItem>
              <SelectItem value="180">Last 6 months</SelectItem>
              <SelectItem value="365">Last 12 months</SelectItem>
              <SelectItem value="730">Last 2 years</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <p className="text-xs text-muted-foreground mb-1">Sort by</p>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-40 rounded-xl" data-testid="best-clients-sort"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="spend">Total Spend</SelectItem>
              <SelectItem value="visits">Visit Count</SelectItem>
              <SelectItem value="avg">Average Spend</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" size="sm" onClick={exportCSV} disabled={!data} className="rounded-xl ml-auto">
          <Download size={14} className="mr-1" /> Export CSV
        </Button>
      </div>

      <Card className="rounded-2xl border shadow-sm overflow-hidden" data-testid="best-clients-card">
        {loading ? (
          <div className="p-12 text-center text-muted-foreground text-sm">Loading…</div>
        ) : (data?.clients?.length || 0) === 0 ? (
          <div className="p-12 text-center text-muted-foreground text-sm">No data for this window</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left p-3 font-medium text-muted-foreground w-8">#</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">Client</th>
                  <th className="text-right p-3 font-medium text-muted-foreground">Visits</th>
                  <th className="text-right p-3 font-medium text-muted-foreground">Total Spend</th>
                  <th className="text-right p-3 font-medium text-muted-foreground">Avg / visit</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">Last Visit</th>
                </tr>
              </thead>
              <tbody>
                {data.clients.map((c, i) => (
                  <tr key={c.client_id} className="border-b border-border/40 last:border-0 hover:bg-muted/20 transition-colors" data-testid={`best-client-row-${c.client_id}`}>
                    <td className="p-3 text-muted-foreground tabular-nums">{i + 1}</td>
                    <td className="p-3">
                      <Link to={`/admin/clients/${c.client_id}`} className="font-medium hover:text-primary hover:underline underline-offset-2 inline-flex items-center gap-1.5">
                        {c.name}
                        {c.status === 'VIP' && <Crown size={12} className="text-amber-500" />}
                      </Link>
                      <p className="text-xs text-muted-foreground">{c.email}</p>
                    </td>
                    <td className="p-3 text-right tabular-nums">{c.visits}</td>
                    <td className="p-3 text-right tabular-nums font-semibold">{fmtGBP(c.total_spent)}</td>
                    <td className="p-3 text-right tabular-nums text-muted-foreground">{fmtGBP(c.avg_spend)}</td>
                    <td className="p-3 text-xs text-muted-foreground whitespace-nowrap">{safeFormat(c.last_visit, 'd MMM yyyy')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

export function LapsedClientsReport() {
  const [minDays, setMinDays] = useState(42);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState({});      // per-row loading
  const [bulkSending, setBulkSending] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/reports/lapsed-clients?min_days=${minDays}&max_days=365&limit=100`);
      setData(res.data);
    } catch (e) { toast.error('Failed to load lapsed clients'); }
    setLoading(false);
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minDays]);

  const sendWinback = async (clientId) => {
    setSending(s => ({ ...s, [clientId]: true }));
    try {
      const res = await axios.post(`${API}/reports/winback/${clientId}`);
      if (res.data.success && res.data.email_status === 'sent') {
        toast.success(`Win-back sent · voucher ${res.data.voucher_code}`);
      } else if (res.data.skipped) {
        toast.warning(res.data.error || 'Cooldown active');
      } else if (res.data.email_status === 'not_sent') {
        toast.warning(`Voucher ${res.data.voucher_code} created — email not configured`);
      } else {
        toast.error(res.data.error || 'Failed to send');
      }
      reload();
    } catch (e) {
      toast.error('Failed to send win-back');
    }
    setSending(s => ({ ...s, [clientId]: false }));
  };

  const sendWinbackBulk = async () => {
    if (!data?.clients?.length) return;
    if (!window.confirm(`Send a win-back email + voucher to all ${data.clients.length} lapsed clients shown? Clients within their cooldown window will be skipped.`)) return;
    setBulkSending(true);
    try {
      const res = await axios.post(`${API}/reports/winback-bulk`, {
        client_ids: data.clients.map(c => c.client_id),
      });
      const r = res.data;
      toast.success(`Win-back: ${r.sent} sent · ${r.skipped} skipped (cooldown) · ${r.failed} failed`);
      reload();
    } catch (e) { toast.error('Bulk send failed'); }
    setBulkSending(false);
  };

  const exportCSV = () => {
    if (!data?.clients) return;
    const rows = [
      ['Lapsed Clients', `${minDays}+ days since last visit`], [],
      ['Name', 'Email', 'Last Visit', 'Days Since', 'Past Visits', 'Total Spent'],
      ...data.clients.map(c => [c.name, c.email, c.last_visit?.slice(0, 10), c.days_since, c.visits, c.total_spent])
    ];
    const csv = rows.map(r => r.map(v => `"${(v ?? '').toString().replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `lapsed-clients-${minDays}d.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const totalLostValue = (data?.clients || []).reduce((s, c) => s + (c.total_spent || 0), 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <p className="text-xs text-muted-foreground mb-1">Lapsed window</p>
          <Select value={String(minDays)} onValueChange={(v) => setMinDays(Number(v))}>
            <SelectTrigger className="w-48 rounded-xl" data-testid="lapsed-window"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="21">21+ days (at risk)</SelectItem>
              <SelectItem value="42">6+ weeks</SelectItem>
              <SelectItem value="90">3+ months</SelectItem>
              <SelectItem value="180">6+ months</SelectItem>
              <SelectItem value="365">1+ year</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="ml-auto flex gap-2">
          <Button
            variant="outline" size="sm" onClick={exportCSV}
            disabled={!data} className="rounded-xl"
          >
            <Download size={14} className="mr-1" /> Export CSV
          </Button>
          <Button
            size="sm" onClick={sendWinbackBulk}
            disabled={bulkSending || !data?.clients?.length}
            data-testid="winback-bulk-btn"
            className="rounded-xl bg-amber-500 hover:bg-amber-600 text-white"
          >
            <Mail size={14} className="mr-1" /> {bulkSending ? 'Sending…' : `Send win-back to all (${data?.clients?.length || 0})`}
          </Button>
        </div>
      </div>

      {data && data.clients.length > 0 && (
        <Card className="p-4 rounded-2xl border bg-amber-50/50 border-amber-200">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <p className="text-xs text-amber-700 font-medium uppercase tracking-wider mb-1">Win-back opportunity</p>
              <p className="text-sm text-amber-900">
                <strong>{data.clients.length}</strong> clients with combined past spend of <strong>{fmtGBP(totalLostValue)}</strong> haven't been seen in {minDays}+ days.
              </p>
            </div>
          </div>
        </Card>
      )}

      <Card className="rounded-2xl border shadow-sm overflow-hidden" data-testid="lapsed-clients-card">
        {loading ? (
          <div className="p-12 text-center text-muted-foreground text-sm">Loading…</div>
        ) : (data?.clients?.length || 0) === 0 ? (
          <div className="p-12 text-center text-muted-foreground text-sm">All clients are up to date — no lapsed clients in this window 🎉</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left p-3 font-medium text-muted-foreground">Client</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">Last Visit</th>
                  <th className="text-right p-3 font-medium text-muted-foreground">Days Since</th>
                  <th className="text-right p-3 font-medium text-muted-foreground">Past Visits</th>
                  <th className="text-right p-3 font-medium text-muted-foreground">Total Spent</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">Action</th>
                </tr>
              </thead>
              <tbody>
                {data.clients.map((c) => (
                  <tr key={c.client_id} className="border-b border-border/40 last:border-0 hover:bg-muted/20 transition-colors" data-testid={`lapsed-client-row-${c.client_id}`}>
                    <td className="p-3">
                      <Link to={`/admin/clients/${c.client_id}`} className="font-medium hover:text-primary hover:underline underline-offset-2">
                        {c.name}
                      </Link>
                      <p className="text-xs text-muted-foreground">{c.email}</p>
                    </td>
                    <td className="p-3 text-xs text-muted-foreground whitespace-nowrap">{safeFormat(c.last_visit, 'd MMM yyyy')}</td>
                    <td className="p-3 text-right tabular-nums">
                      <Badge variant="outline" className={`${c.days_since >= 90 ? 'bg-red-100 text-red-700 border-red-200' : 'bg-amber-100 text-amber-700 border-amber-200'}`}>
                        {c.days_since}d
                      </Badge>
                    </td>
                    <td className="p-3 text-right tabular-nums">{c.visits}</td>
                    <td className="p-3 text-right tabular-nums font-semibold">{fmtGBP(c.total_spent)}</td>
                    <td className="p-3">
                      {(() => {
                        const lastSent = c.last_winback_sent_at;
                        if (lastSent) {
                          const days = Math.floor((Date.now() - new Date(lastSent).getTime()) / (1000 * 60 * 60 * 24));
                          return (
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-[10px]" title={`Last sent ${safeFormat(lastSent, 'd MMM yyyy')}`}>
                              ✓ Sent {days}d ago
                            </Badge>
                          );
                        }
                        return c.email ? (
                          <Button
                            size="sm" variant="outline"
                            onClick={() => sendWinback(c.client_id)}
                            disabled={!!sending[c.client_id]}
                            data-testid={`winback-btn-${c.client_id}`}
                            className="rounded-xl text-xs h-7 border-amber-300 text-amber-700 hover:bg-amber-50"
                          >
                            <Mail size={11} className="mr-1" /> {sending[c.client_id] ? '…' : 'Send win-back'}
                          </Button>
                        ) : <span className="text-xs text-muted-foreground">No email</span>;
                      })()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

export function RetentionCohortsReport() {
  const [months, setMonths] = useState(12);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/reports/retention-cohorts?months=${months}`);
        if (!cancelled) setData(res.data);
      } catch (e) { toast.error('Failed to load cohorts'); }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [months]);

  const heatColor = (pct) => {
    if (pct >= 75) return 'bg-emerald-200 text-emerald-900';
    if (pct >= 50) return 'bg-emerald-100 text-emerald-800';
    if (pct >= 25) return 'bg-amber-100 text-amber-800';
    if (pct > 0)   return 'bg-red-100 text-red-700';
    return 'bg-muted/40 text-muted-foreground';
  };

  const fmtCohort = (ym) => {
    if (!ym) return '';
    const [y, m] = ym.split('-');
    return `${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][parseInt(m, 10) - 1]} ${y.slice(2)}`;
  };

  const maxOffset = data?.max_offsets ?? 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <p className="text-xs text-muted-foreground mb-1">Cohort range</p>
          <Select value={String(months)} onValueChange={(v) => setMonths(Number(v))}>
            <SelectTrigger className="w-40 rounded-xl" data-testid="cohorts-range"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="6">Last 6 months</SelectItem>
              <SelectItem value="12">Last 12 months</SelectItem>
              <SelectItem value="24">Last 24 months</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card className="p-4 rounded-2xl border shadow-sm overflow-hidden" data-testid="cohorts-card">
        <p className="text-xs text-muted-foreground mb-3">
          Each row groups clients by their <strong>first visit month</strong>. Each column shows the % who came back N months later. Higher = stickier cohort.
        </p>
        {loading ? (
          <div className="p-12 text-center text-muted-foreground text-sm">Loading…</div>
        ) : (data?.cohorts?.length || 0) === 0 ? (
          <div className="p-12 text-center text-muted-foreground text-sm">Not enough data yet</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr>
                  <th className="text-left p-2 font-medium text-muted-foreground sticky left-0 bg-card">Cohort</th>
                  <th className="text-right p-2 font-medium text-muted-foreground">New</th>
                  {Array.from({ length: maxOffset }).map((_, i) => (
                    <th key={i} className="text-center p-2 font-medium text-muted-foreground min-w-[60px]">M+{i}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.cohorts.map((row) => (
                  <tr key={row.cohort} className="border-t border-border/40">
                    <td className="p-2 font-medium whitespace-nowrap sticky left-0 bg-card">{fmtCohort(row.cohort)}</td>
                    <td className="p-2 text-right tabular-nums text-muted-foreground">{row.size}</td>
                    {Array.from({ length: maxOffset }).map((_, i) => {
                      const cell = row.offsets[i];
                      if (!cell) return <td key={i} className="p-1.5"></td>;
                      return (
                        <td key={i} className="p-1.5 text-center">
                          <div className={`rounded-md py-1.5 px-1 font-medium tabular-nums ${heatColor(cell.pct)}`} title={`${cell.count} of ${row.size} clients`}>
                            {cell.pct}%
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card className="p-4 rounded-2xl border bg-muted/20">
        <p className="text-xs font-semibold mb-2 flex items-center gap-1">
          <ChevronRight size={12} /> How to read this
        </p>
        <ul className="text-xs text-muted-foreground space-y-1 list-disc pl-5">
          <li><strong>M+0</strong> is always 100% — those are the clients who first visited in that month.</li>
          <li><strong>M+1, M+2…</strong> show the % of that same cohort who came back in that month.</li>
          <li>Cohorts with strong M+1 ≥ 50% mean clients form a habit quickly. M+3+ ≥ 40% indicates true loyalty.</li>
          <li>Recent cohorts have fewer columns simply because not enough months have passed.</li>
        </ul>
      </Card>
    </div>
  );
}
